import * as React from 'react';
import {
  Alert,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { withTranslation } from 'react-i18next';
import DataTable from '../../shared/DataTable';
import MetricCard from '../components/MetricCard';
import { useInventoryMovementsReport } from '../hooks/useReportsApi';

const formatQuantity = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 2 }).format(value);
};

const formatSignedQuantity = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  return `${numeric > 0 ? '+' : ''}${formatQuantity(numeric)}`;
};

const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${Number(day)}.${Number(month)}.${year}`;
};

function InventoryMovementSection({ t, year, month }) {
  const { data, loading, error } = useInventoryMovementsReport(year, month);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  const columns = [
    {
      key: 'date',
      label: t('reports.inventory.date'),
      width: '1%',
      noWrap: true,
      render: (row) => formatDate(row.date),
    },
    {
      key: 'part_name',
      label: t('reports.inventory.part'),
      render: (row) => (
        <Box sx={{ maxWidth: { xs: 160, sm: 240, md: 320 }, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.part_name}
          </Typography>
          <Typography
            sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 11, color: 'text.secondary' }}
          >
            {row.type_display}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'type_display',
      label: t('reports.inventory.transactionType'),
      width: '1%',
      noWrap: true,
      hideBelow: 'sm',
      render: (row) => row.type_display,
    },
    {
      key: 'quantity_change',
      label: t('reports.inventory.quantityChange'),
      align: 'right',
      width: '1%',
      noWrap: true,
      render: (row) => (
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: Number(row.quantity_change) < 0 ? 'error.main' : 'success.main' }}
        >
          {formatSignedQuantity(row.quantity_change)}
        </Typography>
      ),
    },
    {
      key: 'quantity_after',
      label: t('reports.inventory.quantityAfter'),
      align: 'right',
      width: '1%',
      noWrap: true,
      hideBelow: 'md',
      render: (row) => formatQuantity(row.quantity_after),
    },
    {
      key: 'reference_number',
      label: t('reports.inventory.reference'),
      maxWidth: 140,
      hideBelow: 'xl',
      render: (row) => row.reference_number || '-',
    },
    {
      key: 'user_name',
      label: t('reports.inventory.user'),
      maxWidth: 120,
      hideBelow: 'xl',
      render: (row) => row.user_name || '-',
    },
  ];

  return (
    <Box data-testid="reports-inventory-movements">
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={SwapVertIcon}
            label={t('reports.inventory.movementCount')}
            value={data?.total_count}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={AddCircleOutlineIcon}
            label={t('reports.inventory.additions')}
            value={data?.additions}
            format="decimal"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={RemoveCircleOutlineIcon}
            label={t('reports.inventory.deductions')}
            value={data?.deductions}
            format="decimal"
            loading={loading}
          />
        </Grid>
      </Grid>

      {data?.by_type?.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('reports.inventory.byType')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.inventory.transactionType')}</TableCell>
                  <TableCell align="right">{t('reports.inventory.count')}</TableCell>
                  <TableCell align="right">{t('reports.inventory.quantityChange')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.by_type.map((entry) => (
                  <TableRow key={entry.transaction_type}>
                    <TableCell>{entry.type_display}</TableCell>
                    <TableCell align="right">{entry.count}</TableCell>
                    <TableCell align="right">{formatSignedQuantity(entry.quantity_change)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {data?.truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('reports.inventory.truncated', { n: data.rows.length })}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={data?.rows || []}
        loading={loading}
        alwaysVisible
        horizontalScroll
        tableTestId="reports-inventory-movements-table"
        emptyIcon={<SwapVertIcon />}
        emptyTitle={t('reports.inventory.noMovements')}
        emptyDescription={t('reports.inventory.noMovementsDescription')}
      />
    </Box>
  );
}

export default withTranslation()(InventoryMovementSection);
